import * as React from 'react';

import type { ModalRouteData } from 'af-utils/modalNavigation.util';

import GuideMainPage from './GuideMainPage';
import ModalHome from './ModalHome';
import SearchPage from './SearchPage';
import type { PageIcons } from './types';
import UserGuide from './UserGuide';

/**
 * To add a new guide in the code, add title in the files array, the route in the routes and the icon in icons map
 * and the name in the main pages if it should show on the home screen.
 *
 * Add the file to the files. If it is a main page add it to the seperate array, so we can choose to load these first.
 *
 * Add a link to whatever page you need. Instead of spaces use underscore.
 *
 * Make sure that the H1 titles in the page are the same as the file names.
 *
 * If the guide is a main page for part of the platform, eg. Jobs, update the HeaderLoggedIn.tsx to automatically open the right guide instead of the home page.
 */

// Used in home page, pass the spaced string that is displayed at the top of main pages as key, e.g. Tool Repairs, as value pass the link
interface MainPageLinks {
	[page: string]: string;
}

export const mainPages: MainPageLinks = {
	'Jobs': '/jobs',
	'Work Request': '/work_request',
	'Work Orders': '/work_orders',
	'Deliverables': '/deliverables',
	'Orders': '/orders',
	'Purchase Orders': '/purchase_orders',
	'Field Reports': '/field_reports',
	'Accounting': '/accounting',
	'Analytics': '/analytics',
	'Resources': '/resources',
	'Equipment': '/equipment_main',
	'Tool Repairs': '/tool_repairs',
};

export const routes: ModalRouteData[] = [
	// User guide generic routes
	{
		path: '/',
		element: <ModalHome />,
	},
	{
		path: '/search',
		element: <SearchPage />,
	},

	// #region Job routes
	{
		path: '/jobs',
		element: <GuideMainPage icon="icon-jobs" mainPageName="Jobs" mdFile="Jobs/Jobs" />,
	},
	{
		path: '/managing_a_specific_job',
		element: <UserGuide icon="icon-jobs" mdFile="Jobs/Managing_a_Specific_Job" />,
	},
	{
		path: '/attachments_on_job_page',
		element: <UserGuide icon="icon-jobs" mdFile="Jobs/Attachments_on_Job_Page" />,
	},
	{
		path: '/job_hazard_assessment',
		element: <UserGuide icon="icon-jobs" mdFile="Jobs/Job_Hazard_Assessment" />,
	},
	{
		path: '/related_work_orders',
		element: <UserGuide icon="icon-jobs" mdFile="Jobs/Related_Work_Orders" />,
	},
	{
		path: '/job_work_summary',
		element: <UserGuide icon="icon-jobs" mdFile="Jobs/Job_Work_Summary" />,
	},
	{
		path: '/invoice_on_job_page',
		element: <UserGuide icon="icon-jobs" mdFile="Jobs/Invoice_on_Job_Page" />,
	},
	{
		path: '/billing_codes',
		element: <UserGuide icon="icon-jobs" mdFile="Jobs/Billing_Codes" />,
	},
	{
		path: '/materials_on_job_page',
		element: <UserGuide icon="icon-jobs" mdFile="Jobs/Materials_on_Job_Page" />,
	},
	{
		path: '/job_tracking',
		element: <UserGuide icon="icon-jobs" mdFile="Jobs/Job_Tracking" />,
	},
	// #endregion

	// #region Accounting routes
	{
		path: '/accounting',
		element: <GuideMainPage icon="icon-accounting" mainPageName="Accounting" mdFile="Accounting/Accounting" />,
	},
	{
		path: '/exception_report_for_wr',
		element: <UserGuide icon="icon-accounting" mdFile="Accounting/Exception_Report_for_WR" />,
	},
	{
		path: '/invoice_on_accounting_page',
		element: <UserGuide icon="icon-accounting" mdFile="Accounting/Invoice_on_Accounting_Page" />,
	},
	{
		path: '/job_payroll_report',
		element: <UserGuide icon="icon-accounting" mdFile="Accounting/Job_Payroll_Report" />,
	},
	{
		path: '/prevailing_wage_report',
		element: <UserGuide icon="icon-accounting" mdFile="Accounting/Prevailing_Wage_Report" />,
	},
	{
		path: '/per_diem_report',
		element: <UserGuide icon="icon-accounting" mdFile="Accounting/Per_Diem_Report" />,
	},
	// #endregion

	// #region Analytics

	{
		path: '/analytics',
		element: <GuideMainPage icon="icon-accounting" mainPageName="Analytics" mdFile="Analytics/Analytics" />,
	},
	{
		path: '/equipment_utilization',
		element: <UserGuide icon="icon-accounting" mdFile="Analytics/Equipment_Utilization" />,
	},
	{
		path: '/labor_utilization',
		element: <UserGuide icon="icon-accounting" mdFile="Analytics/Labor_Utilization" />,
	},

	// #endregion

	// #region Orders routes
	{
		path: '/orders',
		element: <GuideMainPage icon="icon-cost_code" mainPageName="Orders" mdFile="Orders/Orders" />,
	},
	{
		path: '/creating_a_new_order',
		element: <UserGuide icon="icon-cost_code" mdFile="Orders/Creating_a_New_Order" />,
	},
	{
		path: '/creating_order_for_stock',
		element: <UserGuide icon="icon-cost_code" mdFile="Orders/Creating_Order_For_Stock" />,
	},
	{
		path: '/order_management',
		element: <UserGuide icon="icon-cost_code" mdFile="Orders/Order_Management" />,
	},
	{
		path: '/order_notifications',
		element: <UserGuide icon="icon-cost_code" mdFile="Orders/Order_Notifications" />,
	},
	{
		path: '/order_reports',
		element: <UserGuide icon="icon-cost_code" mdFile="Orders/Order_Reports" />,
	},
	// #endregion

	// #region Purchase order routes
	{
		path: '/purchase_orders',
		element: <GuideMainPage icon="icon-cart" mainPageName="Purchase Orders" mdFile="Purchase_Orders/Purchase_Orders" />,
	},
	{
		path: '/creating_new_purchase_order',
		element: <UserGuide icon="icon-cart" mdFile="Purchase_Orders/Creating_New_Purchase_Order" />,
	},
	{
		path: '/purchase_order_management',
		element: <UserGuide icon="icon-cart" mdFile="Purchase_Orders/Purchase_Order_Management" />,
	},
	{
		path: '/purchase_order_notifications',
		element: <UserGuide icon="icon-cart" mdFile="Purchase_Orders/Purchase_Order_Notifications" />,
	},
	// #endregion

	// #region Field report routes
	{
		path: '/field_reports',
		element: <GuideMainPage icon="icon-daily_report" mainPageName="Field Reports" mdFile="Field_Reports/Field_Reports" />,
	},
	{
		path: '/field_report_work_summary',
		element: <UserGuide icon="icon-daily_report" mdFile="Field_Reports/Field_Report_Work_Summary" />,
	},
	{
		path: '/report_type_view',
		element: <UserGuide icon="icon-daily_report" mdFile="Field_Reports/Report_Type_View" />,
	},
	{
		path: '/time_card_entries',
		element: <UserGuide icon="icon-daily_report" mdFile="Field_Reports/Time_Card_Entries" />,
	},
	{
		path: '/time_sheets',
		element: <UserGuide icon="icon-daily_report" mdFile="Field_Reports/Time_Sheets" />,
	},
	{
		path: '/signature_request_and_report_sending_to_customer',
		element: <UserGuide icon="icon-daily_report" mdFile="Field_Reports/Signature_Request_and_Report_Sending_to_Customer" />,
	},
	{
		path: '/field_report_exporting',
		element: <UserGuide icon="icon-daily_report" mdFile="Field_Reports/Field_Report_Exporting" />,
	},
	{
		path: '/submitting,_approving_and_rejecting_field_report_process',
		element: <UserGuide icon="icon-daily_report" mdFile="Field_Reports/Submitting,_Approving_and_Rejecting_Field_Report_Process" />,
	},
	{
		path: '/condensed_view',
		element: <UserGuide icon="icon-daily_report" mdFile="Field_Reports/Condensed_View" />,
	},
	{
		path: '/attaching_previous_report_types_to_work_orders',
		element: <UserGuide icon="icon-daily_report" mdFile="Field_Reports/Attaching_Previous_Report_Types_To_Work_Orders" />,
	},
	// #endregion

	// #region Tool repair routes
	{
		path: '/tool_repairs',
		element: <GuideMainPage icon="icon-wrench-cog" mainPageName="Tool Repairs" mdFile="Tool_Repairs/Tool_Repairs" />,
	},
	{
		path: '/creating_new_repair_request',
		element: <UserGuide icon="icon-wrench-cog" mdFile="Tool_Repairs/Creating_New_Repair_Request" />,
	},
	{
		path: '/managing_tool_repairs',
		element: <UserGuide icon="icon-wrench-cog" mdFile="Tool_Repairs/Managing_Tool_Repairs" />,
	},
	{
		path: '/repair_request_notifications',
		element: <UserGuide icon="icon-wrench-cog" mdFile="Tool_Repairs/Repair_Request_Notifications" />,
	},
	// #endregion

	// #region Resources routes
	{
		path: '/resources',
		element: <GuideMainPage icon="icon-list" mainPageName="Resources" mdFile="Resources/Resources" />,
	},
	{
		path: '/accessory_category',
		element: <UserGuide icon="icon-list" mdFile="Resources/Accessory_Category" />,
	},
	{
		path: '/location',
		element: <UserGuide icon="icon-list" mdFile="Resources/Location" />,
	},
	{
		path: '/vendors',
		element: <UserGuide icon="icon-list" mdFile="Resources/Vendors" />,
	},
	{
		path: '/classification_code',
		element: <UserGuide icon="icon-list" mdFile="Resources/Classification_Code" />,
	},
	{
		path: '/crew_type',
		element: <UserGuide icon="icon-list" mdFile="Resources/Crew_Type" />,
	},
	{
		path: '/deliverable_resource',
		element: <UserGuide icon="icon-list" mdFile="Resources/Deliverable_Resource" />,
	},
	{
		path: '/division',
		element: <UserGuide icon="icon-list" mdFile="Resources/Division" />,
	},
	{
		path: '/document',
		element: <UserGuide icon="icon-list" mdFile="Resources/Document" />,
	},
	{
		path: '/employee_status',
		element: <UserGuide icon="icon-list" mdFile="Resources/Employee_Status" />,
	},
	{
		path: '/equipment_cost',
		element: <UserGuide icon="icon-list" mdFile="Resources/Equipment_Cost" />,
	},
	{
		path: '/equipment_status',
		element: <UserGuide icon="icon-list" mdFile="Resources/Equipment_Status" />,
	},
	{
		path: '/job_status',
		element: <UserGuide icon="icon-list" mdFile="Resources/Job_Status" />,
	},
	{
		path: '/labor',
		element: <UserGuide icon="icon-list" mdFile="Resources/Labor" />,
	},
	{
		path: '/sales',
		element: <UserGuide icon="icon-list" mdFile="Resources/Sales" />,
	},
	{
		path: '/skill',
		element: <UserGuide icon="icon-list" mdFile="Resources/Skill" />,
	},
	{
		path: '/temp_agencies',
		element: <UserGuide icon="icon-list" mdFile="Resources/Temp_Agencies" />,
	},
	{
		path: '/temporary_labor',
		element: <UserGuide icon="icon-list" mdFile="Resources/Temporary_Labor" />,
	},
	{
		path: '/wage_rate',
		element: <UserGuide icon="icon-list" mdFile="Resources/Wage_Rate" />,
	},
	{
		path: '/materials',
		element: <UserGuide icon="icon-list" mdFile="Resources/Materials" />,
	},
	// #endregion

	// #region Work orders routes
	{
		path: '/work_orders',
		element: <GuideMainPage icon="icon-work_order" mainPageName="Work Orders" mdFile="Work_Orders/Work_Orders" />,
	},
	{
		path: '/creating_a_new_work_order',
		element: <UserGuide icon="icon-work_order" mdFile="Work_Orders/Creating_a_New_Work_Order" />,
	},
	{
		path: '/managing_work_orders',
		element: <UserGuide icon="icon-work_order" mdFile="Work_Orders/Managing_Work_Orders" />,
	},
	{
		path: '/work_order_production_data_and_statistics',
		element: <UserGuide icon="icon-work_order" mdFile="Work_Orders/Work_Order_Production_Data_and_Statistics" />,
	},
	{
		path: '/managing_resources',
		element: <UserGuide icon="icon-work_order" mdFile="Work_Orders/Managing_Resources" />,
	},
	{
		path: '/mechanic_view',
		element: <UserGuide icon="icon-work_order" mdFile="Work_Orders/Mechanic_View" />,
	},
	{
		path: '/display_view_of_schedule_board',
		element: <UserGuide icon="icon-work_order" mdFile="Work_Orders/Display_View_of_Schedule_Board" />,
	},
	{
		path: '/schedule_board_exports',
		element: <UserGuide icon="icon-work_order" mdFile="Work_Orders/Schedule_Board_Exports" />,
	},
	{
		path: '/sending_schedule_board_template',
		element: <UserGuide icon="icon-work_order" mdFile="Work_Orders/Sending_Schedule_Board_Template" />,
	},
	{
		path: '/managing_work_locations',
		element: <UserGuide icon="icon-work_order" mdFile="Work_Orders/Managing_Work_Locations" />,
	},
	{
		path: '/notifying_laborers',
		element: <UserGuide icon="icon-work_order" mdFile="Work_Orders/Notifying_Laborers" />,
	},
	// #endregion

	// #region Equipment routes
	{
		path: '/equipment_main',
		element: <GuideMainPage icon="icon-equipment" mainPageName="Equipment" mdFile="Equipment/Equipment_main" />,
	},
	{
		path: '/accessories',
		element: <UserGuide icon="icon-equipment" mdFile="Equipment/Accessories" />,
	},
	{
		path: '/equipment',
		element: <UserGuide icon="icon-equipment" mdFile="Equipment/Equipment" />,
	},
	{
		path: '/small_tools',
		element: <UserGuide icon="icon-equipment" mdFile="Equipment/Small_Tools" />,
	},
	{
		path: '/deleted_equipment',
		element: <UserGuide icon="icon-equipment" mdFile="Equipment/Deleted_Equipment" />,
	},
	// #endregion

	// #region Deliverable routes
	{
		path: '/deliverables',
		element: <GuideMainPage icon="icon-deliverables" mainPageName="Deliverables" mdFile="Deliverables/Deliverables" />,
	},
	// #endregion

	// #region Work request routes
	{
		path: '/work_request',
		element: <GuideMainPage icon="icon-work_request" mainPageName="Work Request" mdFile="Work_Request/Work_Request" />,
	},
	{
		path: '/changing_the_bid_status',
		element: <UserGuide icon="icon-work_request" mdFile="Work_Request/Changing_the_Bid_Status" />,
	},
	{
		path: '/job_hazard_assessment_on_work_request',
		element: <UserGuide icon="icon-work_request" mdFile="Work_Request/Job_Hazard_Assessment_on_Work_Request" />,
	},
	{
		path: '/managing_a_specific_work_request',
		element: <UserGuide icon="icon-work_request" mdFile="Work_Request/Managing_a_Specific_Work_Request" />,
	},
	{
		path: '/work_request_attachments',
		element: <UserGuide icon="icon-work_request" mdFile="Work_Request/Work_Request_Attachments" />,
	},
	// #endregion
];

export const mainPageMDFiles = [
	'Jobs/Jobs',
	'Accounting/Accounting',
	'Analytics/Analytics',
	'Orders/Orders',
	'Purchase_Orders/Purchase_Orders',
	'Tool_Repairs/Tool_Repairs',
	'Resources/Resources',
	'Work_Orders/Work_Orders',
	'Equipment/Equipment_main',
	'Field_Reports/Field_Reports',
	'Deliverables/Deliverables',
	'Work_Request/Work_Request',
];

export const nonMainPageMDFiles = [
	// #region Job files
	'Jobs/Managing_a_Specific_Job',
	'Jobs/Attachments_on_Job_Page',
	'Jobs/Related_Work_Orders',
	'Jobs/Invoice_on_Job_Page',
	'Jobs/Job_Hazard_Assessment',
	'Jobs/Job_Work_Summary',
	'Jobs/Billing_Codes',
	'Jobs/Materials_on_Job_Page',
	'Jobs/Job_Tracking',
	// #endregion

	// #region Accounting files
	'Accounting/Exception_Report_for_WR',
	'Accounting/Invoice_on_Accounting_Page',
	'Accounting/Job_Payroll_Report',
	'Accounting/Prevailing_Wage_Report',
	'Accounting/Per_Diem_Report',
	// #endregion

	// #region Analytics files
	'Analytics/Equipment_Utilization',
	'Analytics/Labor_Utilization',
	// #endregion

	// #region Orders files
	'Orders/Creating_a_New_Order',
	'Orders/Creating_Order_For_Stock',
	'Orders/Order_Management',
	'Orders/Order_Notifications',
	'Orders/Order_Reports',
	// #endregion

	// #region Purchase Orders files
	'Purchase_Orders/Creating_New_Purchase_Order',
	'Purchase_Orders/Purchase_Order_Management',
	'Purchase_Orders/Purchase_Order_Notifications',
	// #endregion

	// #region Tool repair files
	'Tool_Repairs/Creating_New_Repair_Request',
	'Tool_Repairs/Managing_Tool_Repairs',
	'Tool_Repairs/Repair_Request_Notifications',
	// #endregion

	// #region Resources files
	'Resources/Equipment',
	'Resources/Accessory_Category',
	'Resources/Location',
	'Resources/Vendors',
	'Resources/Classification_Code',
	'Resources/Crew_Type',
	'Resources/Deliverable_Resource',
	'Resources/Division',
	'Resources/Document',
	'Resources/Employee_Status',
	'Resources/Equipment_Cost',
	'Resources/Equipment_Status',
	'Resources/Job_Status',
	'Resources/Labor',
	'Resources/Sales',
	'Resources/Skill',
	'Resources/Temp_Agencies',
	'Resources/Temporary_Labor',
	'Resources/Wage_Rate',
	'Resources/Materials',
	// #endregion

	// #region Work orders files
	'Work_Orders/Creating_a_New_Work_Order',
	'Work_Orders/Managing_Work_Orders',
	'Work_Orders/Work_Order_Production_Data_and_Statistics',
	'Work_Orders/Managing_Resources',
	'Work_Orders/Mechanic_View',
	'Work_Orders/Display_View_of_Schedule_Board',
	'Work_Orders/Schedule_Board_Exports',
	'Work_Orders/Sending_Schedule_Board_Template',
	'Work_Orders/Managing_Work_Locations',
	'Work_Orders/Notifying_Laborers',
	// #endregion

	// #region Equipment files
	'Equipment/Accessories',
	'Equipment/Equipment',
	'Equipment/Small_Tools',
	'Equipment/Deleted_Equipment',
	// #endregion

	// #region Field reports files
	'Field_Reports/Field_Report_Work_Summary',
	'Field_Reports/Report_Type_View',
	'Field_Reports/Time_Card_Entries',
	'Field_Reports/Time_Sheets',
	'Field_Reports/Signature_Request_and_Report_Sending_to_Customer',
	'Field_Reports/Field_Report_Exporting',
	'Field_Reports/Submitting,_Approving_and_Rejecting_Field_Report_Process',
	'Field_Reports/Condensed_View',
	'Field_Reports/Attaching_Previous_Report_Types_To_Work_Orders',
	// #endregion

	// #region Work request files
	'Work_Request/Changing_the_Bid_Status',
	'Work_Request/Job_Hazard_Assessment_on_Work_Request',
	'Work_Request/Managing_a_Specific_Work_Request',
	'Work_Request/Work_Request_Attachments',
	// #endregion
] as const;

export const icons: PageIcons = {
	// #region Job icons
	'Jobs': 'icon-jobs',
	'Managing_a_Specific_Job': 'icon-jobs',
	'Attachments_on_Job_Page': 'icon-jobs',
	'Related_Work_Orders': 'icon-jobs',
	'Invoice_on_Job_Page': 'icon-jobs',
	'Job_Hazard_Assessment': 'icon-jobs',
	'Job_Work_Summary': 'icon-jobs',
	'Billing_Codes': 'icon-jobs',
	'Materials_on_Job_Page': 'icon-jobs',
	'Job_Tracking': 'icon-jobs',
	// #endregion

	// #region Accounting icons
	'Accounting': 'icon-accounting',
	'Exception_Report_for_WR': 'icon-accounting',
	'Invoice_on_Accounting_Page': 'icon-accounting',
	'Job_Payroll_Report': 'icon-accounting',
	'Prevailing_Wage_Report': 'icon-accounting',
	'Per_Diem_Report': 'icon-accounting',
	// #endregion

	// #region Analytics icons
	'Analytics': 'icon-cost_analysis',
	'Equipment_Utilization': 'icon-cost_analysis',
	'Labor_Utilization': 'icon-cost_analysis',
	// #endregion

	// #region Order icons
	'Orders': 'icon-cost_code',
	'Creating_a_New_Order': 'icon-cost_code',
	'Creating_Order_For_Stock': 'icon-cost_code',
	'Order_Management': 'icon-cost_code',
	'Order_Notifications': 'icon-cost_code',
	'Order_Reports': 'icon-cost_code',
	// #endregion

	// #region Purchase Order icons
	'Purchase_Orders': 'icon-cart',
	'Creating_New_Purchase_Order': 'icon-cart',
	'Purchase_Order_Management': 'icon-cart',
	'Purchase_Order_Notifications': 'icon-cart',
	// #endregion

	// #region Tool Repair icons
	'Tool_Repairs': 'icon-wrench-cog',
	'Creating_New_Repair_Request': 'icon-wrench-cog',
	'Managing_Tool_Repairs': 'icon-wrench-cog',
	'Repair_Request_Notifications': 'icon-wrench-cog',
	// #endregion

	// #region Resources icons
	'Resources': 'icon-list',
	'Accessory_Category': 'icon-list',
	'Location': 'icon-list',
	'Vendors': 'icon-list',
	'Classification_Code': 'icon-list',
	'Crew_Type': 'icon-list',
	'Deliverable_Resource': 'icon-list',
	'Division': 'icon-list',
	'Document': 'icon-list',
	'Employee_Status': 'icon-list',
	'Equipment_Cost': 'icon-list',
	'Equipment_Status': 'icon-list',
	'Job_Status': 'icon-list',
	'Labor': 'icon-list',
	'Sales': 'icon-list',
	'Skill': 'icon-list',
	'Temp_Agencies': 'icon-list',
	'Temporary_Labor': 'icon-list',
	'Wage_Rate': 'icon-list',
	'Materials': 'icon-list',
	// #endregion

	// #region Work orders icons
	'Work_Orders': 'icon-work_order',
	'Creating_a_New_Work_Order': 'icon-work_order',
	'Managing_Work_Orders': 'icon-work_order',
	'Work_Order_Production_Data_and_Statistics': 'icon-work_order',
	'Managing_Resources': 'icon-work_order',
	'Mechanic_View': 'icon-work_order',
	'Display_View_of_Schedule_Board': 'icon-work_order',
	'Schedule_Board_Exports': 'icon-work_order',
	'Sending_Schedule_Board_Template': 'icon-work_order',
	'Managing_Work_Locations': 'icon-work_order',
	'Notifying_Laborers': 'icon-work_order',
	// #endregion

	// #region Equipment icons
	'Equipment_main': 'icon-equipment',
	'Accessories': 'icon-equipment',
	'Equipment': 'icon-equipment',
	'Small_Tools': 'icon-equipment',
	'Deleted_Equipment': 'icon-equipment',
	// #endregion

	// #region Field reports icons
	'Field_Reports': 'icon-daily_report',
	'Field_Report_Work_Summary': 'icon-daily_report',
	'Report_Type_View': 'icon-daily_report',
	'Time_Card_Entries': 'icon-daily_report',
	'Time_Sheets': 'icon-daily_report',
	'Signature_Request_and_Report_Sending_to_Customer': 'icon-daily_report',
	'Field_Report_Exporting': 'icon-daily_report',
	'Submitting,_Approving_and_Rejecting_Field_Report_Process': 'icon-daily_report',
	'Field_Reports/Condensed_View': 'icon-daily_report',
	'Field_Reports/Attaching_Previous_Report_Types_To_Work_Orders': 'icon-daily_report',
	// #endregion

	// #region Deliverables icons
	'Deliverables': 'icon-deliverables',
	// #endregion

	// #region Work request icons
	'Work_Request': 'icon-work_request',
	'Changing_the_Bid_Status': 'icon-work_request',
	'Managing_a_Specific_Work_Request': 'icon-work_request',
	'Work_Request_Attachments': 'icon-work_request',
	'Job_Hazard_Assessment_on_Work_Request': 'icon-work_request',
	// #endregion
};
